import {
	getIsHydrationInProgress,
	reportHydrationEnd,
} from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStateHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import { callWhenIdleWithTimeout } from './callWhenIdleWithTimeout.tsx';

type DebugInfo = {
	pendingIds: Record<string, string>;
	globalTimedOut: boolean;
};
let DEBUG_INFO: DebugInfo = {
	pendingIds: {},
	globalTimedOut: false,
};
export const getPendingSubtreeNames = (): string[] => {
	return Object.values(DEBUG_INFO.pendingIds);
};
export const getIsGlobalTimedOut = (): boolean => DEBUG_INFO.globalTimedOut;

type Unsubscribe = () => void;
let clearNextCallback: Unsubscribe | null = null;
const setOrReplaceNextCallback = (createCallback: () => Unsubscribe) => {
	if (clearNextCallback != null) {
		clearNextCallback();
	}
	clearNextCallback = createCallback();
};
let clearGlobalTimeout: Unsubscribe | null = null;

autoBeforeEachTest(() => {
	DEBUG_INFO = {
		pendingIds: {},
		globalTimedOut: false,
	};

	clearNextCallback?.();
	clearNextCallback = null;
	clearGlobalTimeout?.();
	clearGlobalTimeout = null;
});

/**
 * We need access to global hydration state inside React (reactive) and outside React, so have both a store and module state.
 */
type HydrationCompletionState = { isComplete: boolean; pendingIds: Record<string, string> };
const TIMEOUT_AFTER_LAST_SUBTREE_START = 5_000;
const GLOBAL_TIMEOUT = 30_000;
const HYDRATION_COMPLETE_WINDOW_EVENT = 'jira:hydration-complete';
const actions = {
	addPendingSubtree:
		(componentId: string, name: string): Action<HydrationCompletionState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			if (clearGlobalTimeout == null) {
				const timerId = setTimeout(() => {
					DEBUG_INFO.globalTimedOut = true;
					dispatch(actions.complete());
				}, GLOBAL_TIMEOUT);
				clearGlobalTimeout = () => clearTimeout(timerId);
			}
			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				const updatedPendingIds = {
					...previousPendingIds,
					[componentId]: name,
				};
				setState({ pendingIds: updatedPendingIds });
				DEBUG_INFO.pendingIds = updatedPendingIds;
				// Fallback logic to end hydration 5s after the last pending subtree is added, if we get stuck
				setOrReplaceNextCallback(() => {
					const timerId = setTimeout(() => {
						dispatch(actions.complete());
					}, TIMEOUT_AFTER_LAST_SUBTREE_START);
					return () => clearTimeout(timerId);
				});
			}
		},
	completePendingSubtree:
		(componentId: string): Action<HydrationCompletionState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			const previousPendingIds = getState().pendingIds;
			const updatedPendingIds = { ...previousPendingIds };
			delete updatedPendingIds[componentId];
			setState({ pendingIds: updatedPendingIds });
			DEBUG_INFO.pendingIds = updatedPendingIds;
			if (Object.keys(updatedPendingIds).length === 0) {
				setOrReplaceNextCallback(() => {
					return callWhenIdleWithTimeout(() => {
						dispatch(actions.complete());
					});
				});
			}
		},
	complete:
		(): Action<HydrationCompletionState> =>
		({ setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			reportHydrationEnd();
			setState({ isComplete: true });
			clearNextCallback?.();
			clearGlobalTimeout?.();
			if (globalThis.window) {
				globalThis.window.dispatchEvent(new CustomEvent(HYDRATION_COMPLETE_WINDOW_EVENT));
			}
		},
};
const store = createStore<HydrationCompletionState, typeof actions>({
	name: 'hydration-global-completion',
	initialState: () => ({ isComplete: false, pendingIds: {} }),
	actions,
});

/**
 * Indicates if hydration has completed globally - all subtrees have completed hydrating.
 * NB: this only makes sense to request if {@link useIsHydrateRenderMode} is true.
 */
export const useIsGlobalHydrationComplete: () => boolean = createStateHook(store, {
	selector: ({ isComplete }) => isComplete,
});

export const useGlobalHydrationActions: () => {
	addPendingSubtree: (componentId: string, name: string) => void;
	completePendingSubtree: (componentId: string) => void;
} = createActionsHook(store);
